"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Button } from "app/_components/components/Button";
import { Body } from "app/_components/components/Text/Body";
import type { PricesPageProps, TradeFormProps } from "types/prices";
import NextLink from "components/parts/NextLink";
import { URLS } from "constants/common";
import { useTranslations } from "i18n";
import { FILES } from "i18n/constants";
import get from "lodash/get";
import { formatCurrency } from "utils/currencies/formatters";
import { getIcon } from "utils/currencies/icon";
import ArrowsIcon from "./ArrowsIcon";
import Circle from "./Circle";

const dollarsData = [100, 500, 1000, 5000];

const TradeForm = (props: PricesPageProps & TradeFormProps) => {
  const { tradeForm } = props;
  const { coinName, coinFullName } = tradeForm || {};
  const t = useTranslations(FILES.PRICES);

  const askPrice = Number(get(props?.initialOverview?.priceOverview, "ask", 0));
  const [priceValue, setPriceValue] = useState<number | string>(5);
  const [coinValue, setCoinValue] = useState(
    !isNaN(parseFloat(String(priceValue)))
      ? Number((parseFloat(String(priceValue)) / askPrice).toFixed(10))
      : ""
  );
  const [isUSD, setIsUSD] = useState<boolean>(true);

  const handleSwapValues = () => {
    setIsUSD((prev) => !prev);
  };
  const handlePrice = (priceString: string) => {
    // Remove non-numeric characters except for the first decimal point
    const sanitizedPrice = priceString
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");

    if (/^\d*\.?\d*$/.test(sanitizedPrice) || sanitizedPrice === "") {
      setPriceValue(sanitizedPrice);
      setCoinValue(
        sanitizedPrice === ""
          ? ""
          : Number((parseFloat(sanitizedPrice) / askPrice).toFixed(10))
      );
    }
  };

  const handleCoinPrice = (coinPriceString: string) => {
    // Remove non-numeric characters except for the first decimal point
    const sanitizedCoinPrice = coinPriceString
      .replace(/[^0-9.]/g, "")
      .replace(/(\..*)\./g, "$1");

    if (/^\d*\.?\d*$/.test(sanitizedCoinPrice) || sanitizedCoinPrice === "") {
      setCoinValue(sanitizedCoinPrice);
      setPriceValue(
        sanitizedCoinPrice === ""
          ? ""
          : Number((parseFloat(sanitizedCoinPrice) * askPrice).toFixed(10))
      );
    }
  };

  const renderInputForm = ({
    name,
    value,
    handlePrice,
    displayPrice
  }: {
    name: string;
    value: string | number;
    handlePrice: React.ChangeEventHandler<HTMLInputElement>;
    displayPrice: string;
  }) => {
    return (
      <div className="mt-[32px] flex flex-col">
        <div className="flex items-center">
          <input
            className="w-full border-0 !text-center !text-[48px] !leading-[48px] !font-semibold !tracking-[-1.44px] !text-[#010304]"
            data-testid="input"
            name={name}
            type="text"
            value={isUSD ? `$${value}` : ` ${value}`}
            onChange={handlePrice}
          />
          <Button
            variant="tertiary"
            size="icon"
            className="filterIcon"
            onClick={handleSwapValues}
          >
            <ArrowsIcon />
          </Button>
        </div>
        <div className="flex w-full flex-row items-center justify-center">
          <Body
            size="xs"
            className="!mr-[4px] text-[12px] leading-[16px] font-normal text-[#676868]"
          >
            {displayPrice}
          </Body>
          <Circle />
        </div>
      </div>
    );
  };

  return (
    <div className="mb-[48px] rounded-2xl border border-[rgba(0,0,0,0.10)] bg-white p-[20px] shadow-[0_16px_64px_0_rgba(0,0,0,0.08)]">
      <div className="flex flex-row items-center">
        <Image
          width={18}
          height={18}
          alt={`${coinName} logo`}
          src={getIcon(coinName.toLowerCase(), "circle")}
        />
        <Body
          size="md"
          className="!ml-[8px] text-sm leading-5 font-semibold text-[#676868]"
        >
          {t("tradeFormTitle")} {coinName}
        </Body>
      </div>

      {isUSD
        ? renderInputForm({
            name: "USD Amount",
            value: priceValue,
            handlePrice: (e: React.ChangeEvent<HTMLInputElement>) =>
              handlePrice(e.target.value),
            displayPrice:
              Number(coinValue).toFixed(6) +
              " " +
              get(props?.initialOverview, "coin", " ")
          })
        : renderInputForm({
            name: get(props?.initialOverview, "coin", "") + " Amount",
            value: Number(coinValue).toFixed(6),
            handlePrice: (e: React.ChangeEvent<HTMLInputElement>) =>
              handleCoinPrice(e.target.value),
            displayPrice: formatCurrency(priceValue, 2, 2) ?? ""
          })}

      <div className="mt-[32px] flex items-center justify-center gap-[8px] py-[12px]">
        {dollarsData &&
          dollarsData.length > 0 &&
          dollarsData.map((price: number, index: number) => (
            <div
              key={`dollar-price-${index}`}
              onClick={() => handlePrice(String(price))}
              className="inline-flex cursor-pointer items-center justify-center rounded-full bg-[rgba(1,3,4,0.08)] px-[16px] py-[8px]"
            >
              <Body size="sm" className="text-sm leading-[16px] font-semibold">
                ${price}
              </Body>
            </div>
          ))}
      </div>
      <div className="mt-[12px]">
        <NextLink href={URLS.REGISTER} id="register-buy-button">
          <div className="w-full rounded-[600px] bg-black px-[20px] py-[12px]">
            <Body
              size="sm"
              className="text-center leading-[16px] font-semibold text-white"
            >
              {t("buyButton", { coinName: coinFullName })}
            </Body>
          </div>
        </NextLink>
      </div>
    </div>
  );
};

export default TradeForm;
